import { useState, useEffect } from 'react';
import { useStores } from '../models';
import { ExploreElement } from '../models/ExploreElementModel/ExploreElementModel';
import { ExploreElementType } from '../spinnaxBase/data/explore';
import { useDebugStore } from '../models/DebugStore/DebugStore';
import { isMockElement } from '../util/mockDataDetection';

export interface ContestParticipationStatus {
  isParticipating: boolean;
  contest: ExploreElement | null;
  position: number | null;
  runCount: number;
  maxRuns: number;
  contestName: string;
  updateRunCount: () => void;
}

export const useContestParticipation = (): ContestParticipationStatus => {
  const { exploreElementStore, currentUser } = useStores();
  const debugStore = useDebugStore();

  const [participationStatus, setParticipationStatus] =
    useState<ContestParticipationStatus>({
      isParticipating: false,
      contest: null,
      position: null,
      runCount: 0,
      maxRuns: 0,
      contestName: '',
      updateRunCount: () => {},
    });

  const updateRunCount = () => {
    setParticipationStatus((prev) => ({
      ...prev,
      runCount: prev.runCount + 1,
    }));
  };

  useEffect(() => {
    const checkContestParticipation = () => {
      if (!currentUser?.uid) {
        setParticipationStatus({
          isParticipating: false,
          contest: null,
          position: null,
          runCount: 0,
          maxRuns: 0,
          contestName: '',
          updateRunCount: () => {},
        });
        return;
      }

      // Hole alle aktiven Contests, aber filtere Mock/Proxy-Contests raus
      const allContests = exploreElementStore
        .byType(ExploreElementType.Contest)
        .filter((contest) => !isMockElement(contest));

      // Prüfe auf aktive Contests
      const activeContests = allContests.filter((contest) => {
        const now = new Date();
        const startDate = contest.startDate;
        const endDate = contest.endDate;

        // Ensure dates are valid
        if (!startDate || !endDate) return false;

        const start = new Date(startDate);
        const end = new Date(endDate);

        // Check if dates are valid
        if (isNaN(start.getTime()) || isNaN(end.getTime())) return false;

        return now >= start && now <= end;
      });

      // Prüfe ob User in einem aktiven Contest teilnimmt
      for (const contest of activeContests) {
        const rankings = (contest as any).rankings || [];
        const userEntry = rankings.find(
          (entry: any) =>
            entry.userId === currentUser.uid ||
            entry.userName === currentUser.displayName ||
            entry.userId === 'current-user-mock' // For demo purposes
        );

        if (userEntry) {
          const maxRuns = (contest as any).maxRuns || 3;

          setParticipationStatus({
            isParticipating: true,
            contest,
            position: userEntry.position,
            runCount: userEntry.runCount || 0,
            maxRuns,
            contestName: contest.name,
            updateRunCount,
          });
          return;
        }
      }

      // Keine Teilnahme gefunden
      setParticipationStatus({
        isParticipating: false,
        contest: null,
        position: null,
        runCount: 0,
        maxRuns: 0,
        contestName: '',
        updateRunCount: () => {},
      });
    };

    checkContestParticipation();
  }, [exploreElementStore, currentUser, debugStore]);

  return participationStatus;
};
